import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useLoaderData } from 'react-router-dom';
import SummaryVotesGraph from '../../components/SummaryVotesGraph';
import { useTranslation } from 'react-i18next';
import DataCards from '../../components/dataCards/DataCards/DataCards';
import { VotingDataCard } from '../../components/dataCards/dataCardsTemplates/VotingDataCard';
import axios from 'axios';
import VotingDataCardsFilter from '../../components/dataCards/dataCardsFiltering/VotingDataCardsFilter';
import FilterWrapper from '../../components/dataCards/dataCardsFiltering/FilterWrapper';
import Section from '../../components/common/Section/Section';
import Intro from '../../components/common/Intro/Intro';
import SummarySection from '../../components/common/SummarySection/SummarySection';
import { Colors, URLKeys } from '../../Constants';
import { VotesDataResponseType, VotingCardDataType } from '../../types';

// Will be replaced by generated code from BE
type responseDataType = {
    id_hlasovani: number;
    cislo_hlasovani: string;
    cislo_zasedani: string;
    predmet_hlasovani: string;
    vysledek: 'přijato' | 'nepřijato';
    tajne: 0 | 1;
    validni: 0 | 1;
    proceduralni: 0 | 1;
};

function Votings() {
    const { t } = useTranslation(['votings', 'translation']);
    const { municipalitaId, bodyType, zastupitelstvoId } = useParams();
    const urlSearchParams = new URLSearchParams(window.location.search);
    const [data, setData] = useState<VotingCardDataType[]>([]);
    const [sortFunction, setSortFunction] = useState<(a: any, b: any) => number>(() => 0);
    const votingSummaryData = useLoaderData() as VotesDataResponseType;
    const { pocet_hlasovani, pocet_prijatych, pocet_neprijatych, pocet_neplatnych } = votingSummaryData;

    const getData = async () => {
        try {
            const searchParamObject = Object.fromEntries(urlSearchParams.entries());
            const queryString = new URLSearchParams(searchParamObject).toString();
            const { data } = await axios.get<responseDataType[]>(
                `${municipalitaId}/${bodyType}/${zastupitelstvoId}/votes?${queryString}`
            );
            setData(
                data.map((item: responseDataType) => ({
                    voteId: item.id_hlasovani,
                    voteNumber: Number(item.cislo_hlasovani),
                    meetingNumber: item.cislo_zasedani,
                    voteDescription: item.predmet_hlasovani,
                    voteResult: item.vysledek === 'přijato' ? true : false,
                    secret: item.tajne === 1 ? true : false,
                    valid: item.validni === 1 ? true : false,
                    procedural: item.proceduralni === 1 ? true : false
                }))
            );
        } catch (error) {
            console.error(error);
        }
    };
    const getFilteredSearchParams = () => {
        const filteredParams = new URLSearchParams(urlSearchParams);
        filteredParams.delete(URLKeys.page);
        filteredParams.delete(URLKeys.rows);
        filteredParams.delete(URLKeys.sortBy);
        return filteredParams.toString();
    };

    useEffect(() => {
        getData();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [getFilteredSearchParams()]);

    const summarySectionItems = [
        {
            icon: 'stacks',
            text: t('votings-total', {
                ns: 'translation',
                totalCount: pocet_hlasovani
            }),
            nested: false,
            iconAndHighlightColor: Colors.primary
        },
        {
            icon: 'check_box',
            text: `${pocet_prijatych} ${t('approved-proposals', {
                ns: 'translation'
            })}`,
            nested: true,
            highlightSubString: `${pocet_prijatych}`,
            iconAndHighlightColor: Colors.success
        },
        {
            icon: 'disabled_by_default',
            text: `${pocet_neprijatych} ${t('rejected-proposals', {
                ns: 'translation'
            })}`,
            nested: true,
            highlightSubString: `${pocet_neprijatych}`,
            iconAndHighlightColor: Colors.error
        },
        {
            icon: 'indeterminate_check_box',
            text: `${pocet_neplatnych} ${t('invalid-votings', {
                ns: 'translation'
            })}`,
            nested: true,
            highlightSubString: `${pocet_neplatnych}`,
            iconAndHighlightColor: Colors.darkGray
        }
    ];
    if (data !== undefined) {
        return (
            <>
                <Intro title={t('Votings', { ns: 'translation' })} />
                <SummarySection summaryItems={summarySectionItems} />
                <Section title={t('parties-votings', { ns: 'translation' })} subtitle={t('chart-tooltip')}>
                    <SummaryVotesGraph />
                </Section>
                <Section title={'Jednotlivá hlasování'}>
                    <FilterWrapper filterComponent={<VotingDataCardsFilter setFilterFunc={setSortFunction} />} />
                    <DataCards
                        id='voting-cards'
                        renderCardCallback={(cardData: VotingCardDataType) => <VotingDataCard data={cardData} />}
                        getCardUrl={(item) => `${item.voteId}`}
                        getUniqueId={(item) => item.voteId}
                        cardsData={(() => {
                            if (sortFunction) {
                                return data.sort(sortFunction);
                            }
                            return data;
                        })()}
                    />
                </Section>
            </>
        );
    }
}

export default Votings;
